import { playSoundEffect } from '../hooks/useSoundEffects';

/** Abilities that animate but intentionally have no SFX (e.g. Mend wounds). */
const ABILITIES_WITHOUT_SOUND = new Set(['MendWounds', 'Mend wounds']);

const VARIANT_COUNT = 3;
const DEDUPE_WINDOW_MS = 300;
const PLAYED_ANIMATION_KEY_TTL_MS = 5000;

/** @type {Array<{ stem: string, playAt: number }>} */
const queue = [];
/** stem -> last played timestamp */
const playedRecently = new Map();
/** batchKey -> timeout id for TTL cleanup */
const playedBatchKeys = new Map();

let drainTimer = null;

function resolveSoundStem(abilityName) {
  if (!abilityName || ABILITIES_WITHOUT_SOUND.has(abilityName)) {
    return null;
  }
  const stem = abilityName.replace(/\s+/g, '').toLowerCase();
  if (!stem) {
    return null;
  }
  return stem;
}

function pickVariantPath(stem) {
  const variant = Math.floor(Math.random() * VARIANT_COUNT) + 1;
  return `/sounds/ability${stem}${variant}.mp3`;
}

function isDuplicateStemAtTime(stem, playAt) {
  const lastPlayed = playedRecently.get(stem);
  if (lastPlayed != null && playAt - lastPlayed < DEDUPE_WINDOW_MS) {
    return true;
  }
  for (const item of queue) {
    if (item.stem === stem && Math.abs(item.playAt - playAt) < DEDUPE_WINDOW_MS) {
      return true;
    }
  }
  return false;
}

function rememberBatchKey(batchKey) {
  if (!batchKey) {
    return;
  }
  const existingTimeout = playedBatchKeys.get(batchKey);
  if (existingTimeout) {
    window.clearTimeout(existingTimeout);
  }
  playedBatchKeys.set(
    batchKey,
    window.setTimeout(() => {
      playedBatchKeys.delete(batchKey);
    }, PLAYED_ANIMATION_KEY_TTL_MS)
  );
}

function scheduleDrain(delayMs = 0) {
  if (drainTimer != null) {
    return;
  }
  drainTimer = window.setTimeout(() => {
    drainTimer = null;
    drainStep();
  }, Math.max(0, delayMs));
}

function ensureDrain() {
  scheduleDrain(0);
}

function enqueueStemSound(stem, playAt) {
  if (isDuplicateStemAtTime(stem, playAt)) {
    return;
  }
  queue.push({ stem, playAt });
  queue.sort((a, b) => a.playAt - b.playAt);
  ensureDrain();
}

function drainStep() {
  const now = Date.now();

  while (queue.length > 0) {
    const head = queue[0];
    if (now < head.playAt) {
      scheduleDrain(head.playAt - now);
      return;
    }

    queue.shift();

    const lastPlayed = playedRecently.get(head.stem);
    if (lastPlayed != null && Date.now() - lastPlayed < DEDUPE_WINDOW_MS) {
      continue;
    }

    const path = pickVariantPath(head.stem);
    try {
      playSoundEffect(path, 0.6);
    } catch (error) {
      console.error('playAbilityGainSound: failed to play ability sound', { path, error });
    }
    playedRecently.set(head.stem, Date.now());
  }
}

/**
 * Schedule sounds for one ability-gain animation batch on a unit.
 * Each playable ability uses its own clip at its animation delay (may overlap).
 * Same ability stem within {@link DEDUPE_WINDOW_MS} across units -> once.
 *
 * @param {Array<{ ability: string, delayMs?: number }>} entries
 * @param {string|null} [batchKey=null] - Prevents double-play on remount / Strict Mode.
 */
export function scheduleAbilityGainSoundForBatch(entries, batchKey = null) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return;
  }
  if (batchKey && playedBatchKeys.has(batchKey)) {
    return;
  }

  const playable = entries
    .map((entry) => ({
      stem: resolveSoundStem(entry?.ability),
      delayMs: Math.max(0, entry?.delayMs ?? 0),
    }))
    .filter((entry) => entry.stem);

  if (playable.length === 0) {
    return;
  }

  if (batchKey) {
    rememberBatchKey(batchKey);
  }

  const scheduledAt = Date.now();
  for (const { stem, delayMs } of playable) {
    enqueueStemSound(stem, scheduledAt + delayMs);
  }
}

/** @internal Test-only reset */
export function resetAbilityGainSoundStateForTests() {
  queue.length = 0;
  playedRecently.clear();
  for (const timeoutId of playedBatchKeys.values()) {
    window.clearTimeout(timeoutId);
  }
  playedBatchKeys.clear();
  if (drainTimer != null) {
    window.clearTimeout(drainTimer);
    drainTimer = null;
  }
}
